/** Shared input border styles for meeting builder inline validation. */
export function builderInputClassName(hasError, variant = 'default') {
  if (variant === 'large') {
    return [
      'w-full rounded-2xl border px-4 py-4 text-lg text-gray-900 outline-none transition',
      hasError
        ? 'border-red-400 bg-red-50/80 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-100'
        : 'border-gray-200 bg-gray-50 focus:border-gray-300 focus:bg-white focus:ring-4 focus:ring-gray-100',
    ].join(' ')
  }
  if (variant === 'compact') {
    return [
      'w-full rounded-2xl border px-4 py-3 text-gray-900 outline-none transition',
      hasError
        ? 'border-red-400 bg-red-50/80 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-100'
        : 'border-gray-200 bg-gray-50 focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10',
    ].join(' ')
  }
  return [
    'w-full max-w-[8.5rem] rounded-2xl border px-3 py-2 text-sm text-gray-900 outline-none transition',
    hasError
      ? 'border-red-400 bg-red-50/80 focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-100'
      : 'border-gray-200 bg-gray-50 focus:border-[#16c1b2]/50 focus:bg-white focus:ring-4 focus:ring-[#16c1b2]/10',
  ].join(' ')
}

export function builderPanelClassName(hasError, baseClass) {
  return hasError ? `${baseClass} border-red-300 ring-1 ring-red-200/80` : baseClass
}

/** Rithmo gradient border utilities (see index.css `.builder-gradient-border*`). */
export const BUILDER_GRADIENT_BORDER = 'builder-gradient-border'
export const BUILDER_GRADIENT_BORDER_SELECTED = 'builder-gradient-border-selected'
export const BUILDER_GRADIENT_BORDER_HOVER = 'builder-gradient-border-hover'
export const BUILDER_CHOICE_CARD_SELECTED = 'builder-choice-card-selected ring-1 ring-primary-200/70'
export const BUILDER_CHOICE_CARD_UNSELECTED =
  'builder-choice-card-unselected hover:-translate-y-0.5 hover:shadow-md active:translate-y-0'

/** Duration / weekday pills and schedule preset buttons. */
export function durationQuickOptionPillClassName(selected) {
  return [
    'cursor-pointer rounded-full px-2.5 py-1.5 text-xs font-semibold sm:px-3.5 sm:py-2 sm:text-sm',
    'transition-all duration-200 ease-out',
    'hover:-translate-y-0.5 hover:scale-[1.05] active:translate-y-0 active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/80 focus-visible:ring-offset-1',
    selected
      ? `${BUILDER_GRADIENT_BORDER_SELECTED} shadow-md ring-2 ring-primary-300/50 ring-offset-1`
      : [
          BUILDER_GRADIENT_BORDER,
          'text-primary-700',
          BUILDER_GRADIENT_BORDER_HOVER,
          'hover:text-primary-800 hover:shadow-md',
        ].join(' '),
  ].join(' ')
}

/** Primary-outline pill for secondary actions (e.g. Add agenda item). */
export function builderOutlineActionClassName(disabled = false) {
  return [
    'inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-all duration-200 ease-out',
    BUILDER_GRADIENT_BORDER,
    'text-primary-700',
    BUILDER_GRADIENT_BORDER_HOVER,
    'hover:-translate-y-0.5 hover:text-primary-800 hover:shadow-md',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/80 focus-visible:ring-offset-1',
    'active:translate-y-0 active:scale-[0.98]',
    disabled ? 'cursor-not-allowed opacity-50 hover:translate-y-0 hover:shadow-none' : '',
  ].join(' ')
}

/** Meeting-type grid tile — white fill, Rithmo gradient outline. */
export function builderMeetingTypeTileClassName() {
  return [
    'flex min-h-[3.5rem] w-full items-center justify-center rounded-2xl px-4 py-3 text-center',
    BUILDER_GRADIENT_BORDER,
    'text-sm font-semibold leading-snug text-primary-700 shadow-sm',
    'transition-all duration-200 ease-out',
    BUILDER_GRADIENT_BORDER_HOVER,
    'hover:-translate-y-0.5 hover:text-primary-800 hover:shadow-md',
    'active:translate-y-0 active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/80 focus-visible:ring-offset-2',
  ].join(' ')
}

/** Compact primary action (e.g. Add guest) — matches dashboard `badge-accent` (agenda item count). */
export function builderCompactPrimaryButtonClassName() {
  return [
    'inline-flex shrink-0 items-center justify-center rounded-2xl border border-accent-200 bg-accent-100 px-5 py-3',
    'text-sm font-semibold text-accent-700 shadow-sm transition-all duration-200 ease-out',
    'hover:-translate-y-0.5 hover:border-accent-300 hover:bg-accent-200 hover:text-accent-800 hover:shadow-md hover:ring-2 hover:ring-accent-300/50',
    'active:translate-y-0 active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm',
  ].join(' ')
}

/** Duration stepper shell border. */
export function builderDurationStepperShellClassName(hasError) {
  return [
    'inline-flex max-w-full items-stretch overflow-hidden rounded-2xl border-2 bg-white shadow-sm transition',
    hasError ? 'border-red-400 ring-1 ring-red-200/80' : BUILDER_GRADIENT_BORDER,
  ].join(' ')
}
